import { useState, type ReactNode } from 'react'
import type { Rec } from '../../shared/schema'
import { DIAS_CORTOS, MESES, parseDay, today, ymd } from '../lib/format'
import { IconButton } from './ui'

/* ------------------------------- Kanban ------------------------------- */

export function Kanban({ columns, items, groupBy, render, onMove, onCardClick, empty = 'Arrastrá tarjetas acá' }: {
  columns: { key: string; label?: string; tone?: string }[]
  items: Rec[]
  groupBy: (r: Rec) => string
  render: (r: Rec) => ReactNode
  onMove: (r: Rec, col: string) => void
  onCardClick: (r: Rec) => void
  empty?: string
}) {
  const [over, setOver] = useState<string | null>(null)
  const [dragId, setDragId] = useState<string | null>(null)
  return (
    <div className="kanban" style={{ ['--cols' as any]: columns.length }}>
      {columns.map(c => {
        const list = items.filter(r => (groupBy(r) || columns[0].key) === c.key)
        return (
          <section key={c.key}
            className={`kan-col ${over === c.key ? 'is-over' : ''}`}
            onDragOver={e => { e.preventDefault(); setOver(c.key) }}
            onDragLeave={() => setOver(o => (o === c.key ? null : o))}
            onDrop={e => {
              e.preventDefault(); setOver(null)
              const r = items.find(x => x.id === e.dataTransfer.getData('text/plain'))
              if (r && (groupBy(r) || columns[0].key) !== c.key) onMove(r, c.key)
            }}>
            <header className="kan-head"><span className={`dot tone-${c.tone ?? 'sand'}`} />{c.label ?? c.key}<span className="count">{list.length}</span></header>
            <div className="kan-list">
              {list.map(r => (
                <article key={r.id} className={`kan-card ${dragId === r.id ? 'is-drag' : ''}`} draggable
                  onDragStart={e => { e.dataTransfer.setData('text/plain', r.id); e.dataTransfer.effectAllowed = 'move'; setDragId(r.id) }}
                  onDragEnd={() => setDragId(null)}
                  onClick={() => onCardClick(r)}
                  onKeyDown={e => e.key === 'Enter' && onCardClick(r)}
                  tabIndex={0}>
                  {render(r)}
                </article>
              ))}
              {!list.length && <div className="kan-empty">{empty}</div>}
            </div>
          </section>
        )
      })}
    </div>
  )
}

/* ----------------------------- Calendarios ----------------------------- */

export function startOfWeek(d: Date) { const x = new Date(d); const dow = (x.getDay() + 6) % 7; x.setDate(x.getDate() - dow); x.setHours(0, 0, 0, 0); return x }
export function addDays(d: Date, n: number) { const x = new Date(d); x.setDate(x.getDate() + n); return x }

export function useCursor(initial = new Date()) {
  const [cursor, setCursor] = useState(() => { const d = new Date(initial); d.setHours(0, 0, 0, 0); return d })
  return { cursor, setCursor }
}

export function CalNav({ label, onPrev, onNext, onToday }: { label: string; onPrev: () => void; onNext: () => void; onToday: () => void }) {
  return (
    <div className="cal-nav">
      <IconButton icon="left" label="Anterior" onClick={onPrev} />
      <button type="button" className="cal-today" onClick={onToday}>Hoy</button>
      <IconButton icon="right" label="Siguiente" onClick={onNext} />
      <h2 className="cal-label">{label}</h2>
    </div>
  )
}

export const monthLabel = (d: Date) => `${MESES[d.getMonth()].replace(/^./, c => c.toUpperCase())} ${d.getFullYear()}`

/** Calendario mensual con arrastrar y soltar entre días */
export function MonthGrid({ month, items, dateOf, render, onMove, onDayClick, onItemClick, maxPerDay = 3 }: {
  month: Date
  items: Rec[]
  dateOf: (r: Rec) => string | undefined
  render: (r: Rec) => ReactNode
  onMove?: (r: Rec, day: string) => void
  onDayClick?: (day: string) => void
  onItemClick: (r: Rec) => void
  maxPerDay?: number
}) {
  const [over, setOver] = useState<string | null>(null)
  const first = new Date(month.getFullYear(), month.getMonth(), 1)
  const start = startOfWeek(first)
  const weeks = Math.ceil(((first.getDay() + 6) % 7 + new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate()) / 7)
  const t = today()
  const byDay = new Map<string, Rec[]>()
  for (const r of items) { const d = dateOf(r)?.slice(0, 10); if (d) byDay.set(d, [...(byDay.get(d) ?? []), r]) }
  for (const [k, l] of byDay) byDay.set(k, l.sort((a, b) => String(dateOf(a)).localeCompare(String(dateOf(b)))))
  return (
    <div className="month">
      <div className="month-head">{DIAS_CORTOS.map(d => <div key={d}>{d}</div>)}</div>
      <div className="month-grid" style={{ gridTemplateRows: `repeat(${weeks}, minmax(104px, auto))` }}>
        {Array.from({ length: weeks * 7 }, (_, i) => {
          const d = addDays(start, i)
          const key = ymd(d)
          const list = byDay.get(key) ?? []
          const outside = d.getMonth() !== month.getMonth()
          return (
            <div key={key} className={`day ${outside ? 'outside' : ''} ${key === t ? 'is-today' : ''} ${over === key ? 'is-over' : ''}`}
              onDragOver={onMove && (e => { e.preventDefault(); setOver(key) })}
              onDragLeave={() => setOver(o => (o === key ? null : o))}
              onDrop={onMove && (e => {
                e.preventDefault(); setOver(null)
                const r = items.find(x => x.id === e.dataTransfer.getData('text/plain'))
                if (r && dateOf(r)?.slice(0, 10) !== key) onMove(r, key)
              })}
              onClick={e => { if (e.target === e.currentTarget || (e.target as HTMLElement).classList.contains('day-num')) onDayClick?.(key) }}>
              <span className="day-num">{d.getDate()}</span>
              <div className="day-items">
                {list.slice(0, maxPerDay).map(r => (
                  <button type="button" key={r.id} className="day-item" draggable={!!onMove}
                    onDragStart={e => { e.dataTransfer.setData('text/plain', r.id) }}
                    onClick={() => onItemClick(r)}>{render(r)}</button>
                ))}
                {list.length > maxPerDay && <span className="day-more">+{list.length - maxPerDay} más</span>}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/** Vista de semana o de un día: columnas por día con los elementos en orden */
export function DaysColumns({ start, days, items, dateOf, render, onMove, onDayClick, onItemClick }: {
  start: Date
  days: number
  items: Rec[]
  dateOf: (r: Rec) => string | undefined
  render: (r: Rec) => ReactNode
  onMove?: (r: Rec, day: string) => void
  onDayClick?: (day: string) => void
  onItemClick: (r: Rec) => void
}) {
  const [over, setOver] = useState<string | null>(null)
  const t = today()
  return (
    <div className="week" style={{ ['--days' as any]: days }}>
      {Array.from({ length: days }, (_, i) => {
        const d = addDays(start, i)
        const key = ymd(d)
        const list = items.filter(r => dateOf(r)?.slice(0, 10) === key).sort((a, b) => String(dateOf(a)).localeCompare(String(dateOf(b))))
        return (
          <section key={key} className={`week-col ${key === t ? 'is-today' : ''} ${over === key ? 'is-over' : ''}`}
            onDragOver={onMove && (e => { e.preventDefault(); setOver(key) })}
            onDragLeave={() => setOver(o => (o === key ? null : o))}
            onDrop={onMove && (e => {
              e.preventDefault(); setOver(null)
              const r = items.find(x => x.id === e.dataTransfer.getData('text/plain'))
              if (r) onMove(r, key)
            })}>
            <header className="week-head" onClick={() => onDayClick?.(key)}>
              <span>{DIAS_CORTOS[(d.getDay() + 6) % 7]}</span><strong>{d.getDate()}</strong>
            </header>
            <div className="week-list">
              {list.map(r => (
                <button type="button" key={r.id} className="week-item" draggable={!!onMove}
                  onDragStart={e => e.dataTransfer.setData('text/plain', r.id)} onClick={() => onItemClick(r)}>{render(r)}</button>
              ))}
              {onDayClick && <button type="button" className="week-add" onClick={() => onDayClick(key)} aria-label={`Agregar el ${d.getDate()}`}>+</button>}
            </div>
          </section>
        )
      })}
    </div>
  )
}

export { parseDay }
